import { GYM_MEMBER_LIMIT_MAX, type AdminGymDetail } from '@steado/admin-contract/gyms';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { FieldRow, FieldRows, Panel } from '@/components/panel/panels';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';

import { gymDetailQueryOptions, updateMemberLimit } from './gym-detail-query';
import { memberLimitIssue } from './member-limit';

interface MemberLimitPanelProps {
  readonly gym: AdminGymDetail;
}

/**
 * How many members the gym may have. Steado sets it after talking to the gym;
 * at the limit, members can still ask to join, but the owner cannot approve
 * them. Trainers never count.
 */
export function MemberLimitPanel({ gym }: MemberLimitPanelProps) {
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const left = Math.max(0, gym.memberLimit - gym.memberCount);

  return (
    <>
      <Panel
        title="Member limit"
        description="How many members this gym may have. Trainers don’t count."
        bleed
        {...(can.mutate
          ? {
              action: (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  Change limit
                </Button>
              ),
            }
          : {})}
        footer={
          <span className="text-xs text-muted-foreground">
            {can.mutate
              ? 'At the limit, people can still ask to join, but the owner can’t approve them.'
              : 'Changing the limit needs the admin role.'}
          </span>
        }
      >
        <FieldRows>
          <FieldRow
            label="Members"
            value={`${String(gym.memberCount)} of ${String(gym.memberLimit)}`}
            mono
            {...(left === 0 ? ({ tone: 'warn' } as const) : {})}
          />
          <FieldRow label="Places left" value={String(left)} mono />
        </FieldRows>
      </Panel>
      {editing ? (
        <MemberLimitDialog
          gym={gym}
          onClose={() => {
            setEditing(false);
          }}
        />
      ) : null}
    </>
  );
}

function MemberLimitDialog({ gym, onClose }: { readonly gym: AdminGymDetail; readonly onClose: () => void }) {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const inputId = useId();
  const issueId = useId();
  const [text, setText] = useState(String(gym.memberLimit));
  const issue = memberLimitIssue(text, gym.memberCount);

  const save = useMutation({
    mutationFn: () => updateMemberLimit(api, gym.id, Number(text)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: gymDetailQueryOptions(api, gym.id).queryKey });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'gyms'] });
      onClose();
    },
    retry: false,
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose();
      }}
    >
      <DialogContent aria-label="Change member limit" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change member limit</DialogTitle>
          <DialogDescription>
            {gym.name} has {String(gym.memberCount)} {gym.memberCount === 1 ? 'member' : 'members'} now. The
            change applies at once and is recorded in Admin activity.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (issue === null && !save.isPending) save.mutate();
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
              Member limit
            </Label>
            <Input
              id={inputId}
              inputMode="numeric"
              autoComplete="off"
              className="font-mono"
              value={text}
              aria-invalid={issue !== null}
              {...(issue === null ? {} : { 'aria-describedby': issueId })}
              onChange={(event) => {
                setText(event.target.value);
              }}
            />
            {issue === null ? (
              <span className="text-xs text-muted-foreground">
                From {String(Math.max(1, gym.memberCount))} to {GYM_MEMBER_LIMIT_MAX.toLocaleString('en-IN')}.
              </span>
            ) : (
              <span id={issueId} className="text-xs text-err-fg">
                {issue}
              </span>
            )}
          </div>
          {save.isError ? (
            <p role="alert" className="text-[13px] leading-relaxed text-err-fg">
              {save.error.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={save.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={issue !== null || save.isPending || Number(text) === gym.memberLimit}
            >
              {save.isPending ? 'Saving…' : 'Save limit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
